import { nanoid } from 'nanoid'
import { type ActionItem, showToast, toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { DayjsFormats, logger } from 'shared/lib'
import dayjs from 'shared/lib/dayjs'
import type { PowerDialer } from 'entities/PowerDialer/model/PowerDialer'
import { PowerDialerStatus } from 'entities/PowerDialer/api/types'
import { PowerDialerApi } from 'entities/PowerDialer/api/powerDialer'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { getToastConfigByPowerDialerStatus } from 'widgets/PowerDialer/lib/getToastConfigByPowerDialerStatus'
import { PowerDialerEditorStore } from 'widgets/PowerDialer/ui/PowerDialerEditor/store/powerDialerEditorStore'
import { type CallPopUpGlobalStore } from 'widgets/CallPopUp'

const makeStartAction = (
  powerDialer: PowerDialer,
  callPopUpGlobalStore: CallPopUpGlobalStore
): ActionItem => ({
  key: 'start',
  icon: 'play',
  text: 'Start',
  onAction: async () => {
    try {
      await callPopUpGlobalStore.openPowerDialer(powerDialer.id)
    } catch (e) {
      logger.error(e)
    }
  },
})

const makeResumeAction = (
  powerDialer: PowerDialer,
  callPopUpGlobalStore: CallPopUpGlobalStore
): ActionItem => ({
  key: 'resume',
  icon: 'play',
  text: 'Resume',
  onAction: async () => {
    try {
      await callPopUpGlobalStore.openPowerDialer(powerDialer.id)
      showToast(getToastConfigByPowerDialerStatus(PowerDialerStatus.InProgress))
    } catch (e) {
      logger.error(e)
    }
  },
})

const makeRefreshAContactsAction = (
  powerDialer: PowerDialer,
  fromList: boolean,
  callPopUpGlobalStore: CallPopUpGlobalStore,
  entities: IEntitiesStore
): ActionItem => ({
  key: 'refresh',
  icon: 'refresh',
  text: 'Refresh contacts',
  buttonProps: {
    tooltipProps: {
      type: 'description',
      placement: fromList ? 'left' : 'top',
      label: 'Last refreshed on',
      desc: powerDialer.lastRefreshed
        ? dayjs(powerDialer.lastRefreshed).format(DayjsFormats.fullWithAtDash2)
        : 'N/A',
    },
  },
  onAction: async () => {
    try {
      await new PowerDialerEditorStore(callPopUpGlobalStore, entities).handleRefreshContacts(
        powerDialer,
        fromList ? 'table' : 'statistics'
      )
    } catch (e) {
      logger.error(e)
    }
  },
})

const makeStopAction = (powerDialer: PowerDialer): ActionItem => {
  const onStop = () => {
    const id = nanoid()
    modalStore.addModal({
      id,
      type: ModalTypeList.ALERT,
      title: 'Are you sure?',
      desc: "If stopped you won't be able to continue the session",
      primaryAction: {
        text: 'Stop anyway',
        onAction: async () => {
          try {
            await PowerDialerApi.changePowerDialerStatus(powerDialer.id, PowerDialerStatus.Finished)
            showToast(getToastConfigByPowerDialerStatus(PowerDialerStatus.Finished))
            modalStore.closeModal(id)
          } catch (e) {
            logger.error(e)
          }
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(id)
        },
      },
    })
  }
  return {
    key: 'stop',
    icon: 'stop',
    text: 'Stop',
    onAction: onStop,
  }
}

const makeReactivateAction = (powerDialer: PowerDialer): ActionItem => {
  return {
    key: 'reactivate',
    icon: 'phoneRightArrows',
    text: 'Reactivate',
    onAction: async () => {
      try {
        await PowerDialerApi.changePowerDialerStatus(powerDialer.id, PowerDialerStatus.Paused)
        toastStore.add({
          type: 'success',
          title: 'Power Dialer campaign reactivated',
        })
      } catch (e) {
        toastStore.add({
          type: 'error',
          title: 'Unable to restart Power Dialer campaign',
        })
        logger.error(e)
      }
    },
  }
}

const makeEditAction = (
  powerDialer: PowerDialer,
  callPopUpGlobalStore: CallPopUpGlobalStore,
  entities: IEntitiesStore
): ActionItem => {
  return {
    key: 'edit',
    icon: 'edit',
    text: 'Edit',
    onAction: async () =>
      await new PowerDialerEditorStore(callPopUpGlobalStore, entities).handleEditPowerDialer(
        powerDialer
      ),
  }
}

const makeDuplicateAction = (
  powerDialer: PowerDialer,
  callPopUpGlobalStore: CallPopUpGlobalStore,
  entities: IEntitiesStore
): ActionItem => ({
  key: 'duplicate',
  icon: 'copy',
  text: 'Duplicate',
  onAction: async () =>
    await new PowerDialerEditorStore(callPopUpGlobalStore, entities).handleDuplicatePowerDialer(
      powerDialer
    ),
})

const makeDeleteAction = (powerDialer: PowerDialer, onSuccess: () => void): ActionItem => {
  const onDelete = () => {
    const id = nanoid()
    modalStore.addModal({
      id,
      type: ModalTypeList.ALERT,
      title: 'Delete Power Dialer campaign?',
      desc: 'This action cannot be undone',
      primaryAction: {
        text: 'Delete',
        onAction: async () => {
          try {
            await PowerDialerApi.deletePowerDialer(powerDialer.id)
            toastStore.add({
              type: 'success',
              title: 'Power Dialer campaign deleted',
            })
            modalStore.closeModal(id)
            onSuccess && onSuccess()
          } catch (e) {
            logger.error(e)
          }
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(id)
        },
      },
    })
  }
  return {
    key: 'delete',
    icon: 'delete',
    text: 'Delete',
    onAction: onDelete,
  }
}

type IGetPowerDialerActionsConfig = {
  onSuccessDelete?: () => void
  fromList?: boolean
}

export const getPowerDialerActions = (
  powerDialer: PowerDialer,
  { onSuccessDelete = () => {}, fromList = false }: IGetPowerDialerActionsConfig,
  callPopUpGlobalStore: CallPopUpGlobalStore,
  entities: IEntitiesStore
): ActionItem[] => {
  const startAction = makeStartAction(powerDialer, callPopUpGlobalStore)
  const resumeAction = makeResumeAction(powerDialer, callPopUpGlobalStore)
  const reactivateAction = makeReactivateAction(powerDialer)
  const stopAction = makeStopAction(powerDialer)
  const editAction = makeEditAction(powerDialer, callPopUpGlobalStore, entities)
  const duplicateAction = makeDuplicateAction(powerDialer, callPopUpGlobalStore, entities)
  const deleteAction = makeDeleteAction(powerDialer, onSuccessDelete)
  const refreshContactsAction = makeRefreshAContactsAction(
    powerDialer,
    fromList,
    callPopUpGlobalStore,
    entities
  )
  const conditionalRefreshContactsAction =
    powerDialer.isOnlyManualSelection || !fromList ? [] : [refreshContactsAction]
  const conditionalReactivateAction = powerDialer.isOnlyManualSelection ? [] : [reactivateAction]

  switch (powerDialer.status) {
    case 'in_progress':
      return conditionalRefreshContactsAction
    case 'paused':
      return [
        resumeAction,
        ...conditionalRefreshContactsAction,
        stopAction,
        duplicateAction,
        deleteAction,
      ]
    case 'finished':
      return [...conditionalReactivateAction, duplicateAction, deleteAction]
    case 'draft':
      return [
        startAction,
        ...conditionalRefreshContactsAction,
        editAction,
        duplicateAction,
        deleteAction,
      ]
    default:
      return []
  }
}
